import { useCallback, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  HStack,
  Text,
  Textarea,
} from '@chakra-ui/react'
import { Send } from 'lucide-react'
import { TicketService } from '@/lib/services/ticket'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'

const MAX_CHARS = 2000

type Props = {
  ticketId: string
  parentId?: number
  onSubmitted?: () => void
  onCancel?: () => void
  compact?: boolean
  placeholder?: string
  submitLabel?: string
}

export default function CommentComposer({
  ticketId,
  parentId,
  onSubmitted,
  onCancel,
  compact = false,
  placeholder = 'Write a comment or internal note…',
  submitLabel = 'Post',
}: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [content, setContent] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const maxHeight = 6 * 24
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
  }, [])

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (!trimmed) return

    setSubmitting(true)
    const res = await TicketService.createTicketUpdate(ticketId, {
      content: trimmed,
      type: isInternal ? 'note' : 'comment',
      is_internal: isInternal,
      ...(parentId != null ? { parent_id: parentId } : {}),
    })
    setSubmitting(false)

    if (res.success) {
      showSuccess(parentId != null ? 'Reply posted' : isInternal ? 'Internal note added' : 'Comment added')
      setContent('')
      setIsInternal(false)
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
      onSubmitted?.()
      onCancel?.()
    } else {
      showError(res.message || 'Failed to post comment')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      void handleSubmit()
    }
  }

  const charCount = content.length
  const atLimit = charCount > MAX_CHARS

  return (
    <Box
      flexShrink={0}
      pt={compact ? 0 : 4}
      mt={compact ? 0 : 2}
      borderTop={compact ? 'none' : '1px solid'}
      borderColor={colors.borderLight}
    >
      <FormControl>
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              setContent(e.target.value)
            }
            adjustHeight()
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          size="sm"
          rows={compact ? 2 : 2}
          resize="none"
          overflow="hidden"
          bg={colors.inputBg}
          borderColor={colors.inputBorder}
          _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
        />
      </FormControl>
      <Flex mt={2} justify="space-between" align="center" gap={3} flexWrap="wrap">
        <HStack spacing={4} flexWrap="wrap">
          <Checkbox
            size="sm"
            isChecked={isInternal}
            onChange={(e) => setIsInternal(e.target.checked)}
            colorScheme="blue"
          >
            <Text fontSize="xs" color={colors.textMuted}>
              Internal note (not visible to customer)
            </Text>
          </Checkbox>
          <Text fontSize="xs" color={colors.textMuted}>
            Ctrl/Cmd+Enter
          </Text>
          <Text fontSize="xs" color={atLimit ? 'red.500' : colors.textMuted}>
            {charCount} / {MAX_CHARS}
          </Text>
        </HStack>
        <HStack spacing={2}>
          {onCancel ? (
            <Button size="sm" variant="ghost" onClick={onCancel} isDisabled={submitting}>
              Cancel
            </Button>
          ) : null}
          <Button
            leftIcon={<Send size={14} />}
            colorScheme="blue"
            size="sm"
            onClick={() => void handleSubmit()}
            isLoading={submitting}
            isDisabled={!content.trim() || atLimit}
          >
            {submitLabel}
          </Button>
        </HStack>
      </Flex>
    </Box>
  )
}
